import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Button,
  Grid,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowCircleLeft,
  faArrowCircleRight,
} from "@fortawesome/free-solid-svg-icons";
import { Img } from "react-image";
import { S3Client, GetObjectCommand } from "@aws-sdk/client-s3";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { UPLOADS_BUCKET, API_URL } from "../../config";
import { toast } from "react-toastify";
import { CollectionsApi } from "../../services/api/api";

// Video extensions supported by the system
const VIDEO_EXTENSIONS = new Set(['.mp4', '.mov', '.avi', '.webm', '.mkv']);

// Helper function to check if a file is a video
const isVideoFile = (fileName) => {
  if (!fileName) return false;
  const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
  return VIDEO_EXTENSIONS.has(extension);
};

const ImagePreviewModal = ({
  open,
  onClose,
  media,
  initialIndex,
  collectionDetails,
  awsCredentials,
  userId,
  collectionId,
  versionTime,
  token, // Add token for API calls
  isAdmin = false, // Add admin flag to determine which delete endpoint to use
  onMediaDeleted, // Callback to refresh the parent component after deletion (optional)
}) => {
  const [currentIndex, setCurrentIndex] = useState(null);
  const [currentImage, setCurrentImage] = useState(null);
  const [loadingImage, setLoadingImage] = useState(false);
  const [showProcessingScreen, setShowProcessingScreen] = useState(false);
  // SS-461: when the user opens a FAILED media item, we render a recovery
  // panel instead of closing the modal. This is the only way to surface
  // Redrive/Delete affordances on failed tiles (per QA feedback).
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [recoveryInFlight, setRecoveryInFlight] = useState(false);

  useEffect(() => {
    console.log("ImagePreviewModal useEffect:", { open, initialIndex, media: media?.length });
    if (open && media && media.length > 0 && initialIndex !== null && initialIndex >= 0 && initialIndex < media.length) {
      const mediaItem = media[initialIndex];
      console.log("Selected mediaItem:", mediaItem);
      if (mediaItem && (mediaItem.status === "SUCCESS" || mediaItem.status === "PROCESSING" || mediaItem.status === "SUCCESS_NO_OPTIMIZATION")) {
        setCurrentIndex(initialIndex);
        setRecoveryMode(false);
        getImageUrl(mediaItem, initialIndex);
      } else if (mediaItem && mediaItem.status === "FAILED") {
        // SS-461: open the modal in recovery mode so the user sees Redrive + Delete.
        setCurrentIndex(initialIndex);
        setCurrentImage({ fileName: mediaItem.fileName || mediaItem.f, status: 'FAILED' });
        setRecoveryMode(true);
      } else if (mediaItem) {
        const statusMessage = "This media is still processing. Please check back later.";
        toast.warn(statusMessage, { autoClose: 10000 });
        onClose();
      }
    } else {
      console.log("Modal conditions not met:", {
        open,
        hasMedia: media && media.length > 0,
        validIndex: initialIndex !== null && initialIndex >= 0 && initialIndex < (media?.length || 0)
      });
    }
  }, [open, initialIndex, media]);

  // Add keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!open) return;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          goToPrevious();
          break;
        case 'ArrowRight':
          event.preventDefault();
          goToNext();
          break;
        case 'Escape':
          event.preventDefault();
          handleClose();
          break;
        default:
          break;
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, currentIndex, media]);

  const getImageUrl = async (mediaItem, index) => {
    setLoadingImage(true);
    setShowProcessingScreen(false);
    let key;
    const mediaFileName = mediaItem.fileName || mediaItem.f;
    const isVideo = isVideoFile(mediaFileName);

    if (collectionDetails.createdAt < versionTime) {
      // Legacy: use original file location
      key = `collections/${userId}/${collectionId}/${mediaFileName}`;
    } else {
      // Modern: try optimized version first, fallback to original if needed
      if (isVideo) {
        // For videos, keep original extension in optimized folder
        const fileNameWithoutExt = mediaFileName.replace(/\.[^/.]+$/, "");
        const fileExtension = mediaFileName.substring(mediaFileName.lastIndexOf('.'));
        key = `collections/${userId}/${collectionId}/optimized/${fileNameWithoutExt}${fileExtension}`;
      } else {
        // For images, use .jpg extension in optimized folder
        const filename = mediaFileName.replace(/\.[^/.]+$/, "") + ".jpg";
        key = `collections/${userId}/${collectionId}/optimized/${filename}`;
      }
    }

    try {
      const s3 = new S3Client({
        region: "us-east-1",
        credentials: awsCredentials,
      });
      
      // Try optimized version first
      let command = new GetObjectCommand({
        Bucket: UPLOADS_BUCKET,
        Key: key,
      });
      
      try {
        const url = await getSignedUrl(s3, command, { expiresIn: 3600 });
        setCurrentImage({ 
          url, 
          fileName: mediaFileName, 
          isOptimized: collectionDetails.createdAt >= versionTime,
          status: mediaItem.status 
        });
      } catch (optimizedError) {
        // If optimized version fails and we're not in legacy mode, handle differently for videos vs images
        if (collectionDetails.createdAt >= versionTime) {
          console.log("Optimized version not available:", optimizedError);
          
          if (isVideo && mediaItem.status === "PROCESSING") {
            // For processing videos, show the processing screen instead of trying original
            setShowProcessingScreen(true);
            setCurrentImage({ 
              fileName: mediaFileName, 
              isOptimized: false,
              status: mediaItem.status,
              isProcessing: true
            });
          } else {
            // For images or non-processing videos, try original
            const originalKey = `collections/${userId}/${collectionId}/${mediaFileName}`;
            command = new GetObjectCommand({
              Bucket: UPLOADS_BUCKET,
              Key: originalKey,
            });
            
            try {
              const url = await getSignedUrl(s3, command, { expiresIn: 3600 });
              setCurrentImage({ 
                url, 
                fileName: mediaFileName, 
                isOptimized: false,
                status: mediaItem.status 
              });
              
              // Show informational toast for videos that fell back to original
              if (isVideo && mediaItem.status !== "PROCESSING") {
                toast.info("Showing original quality version.", { autoClose: 3000 });
              }
            } catch (originalError) {
              console.error("Error fetching original file from S3:", originalError);
              
              // If we can't get original either and it's a video, show processing screen
              if (isVideo) {
                setShowProcessingScreen(true);
                setCurrentImage({ 
                  fileName: mediaFileName, 
                  isOptimized: false,
                  status: mediaItem.status,
                  isProcessing: true
                });
              } else {
                throw originalError;
              }
            }
          }
        } else {
          throw optimizedError;
        }
      }
    } catch (err) {
      console.error("Error fetching image from S3:", err);
      
      // If it's a video and we failed to load, show processing screen
      if (isVideo) {
        setShowProcessingScreen(true);
        setCurrentImage({ 
          fileName: mediaFileName, 
          isOptimized: false,
          status: mediaItem.status || "PROCESSING",
          isProcessing: true
        });
      } else {
        toast.error("Failed to load media file.");
      }
    } finally {
      setLoadingImage(false);
    }
  };

  const setAsPreviewImage = async () => {
    if (!currentImage || currentIndex === null || !media || !media[currentIndex]) {
      toast.error("Unable to set preview image");
      return;
    }

    try {
      const mediaItem = media[currentIndex];
      const mediaFileName = mediaItem.fileName || mediaItem.f;
      
      // Construct the S3 key for the optimized image
      let previewImageKey;
      if (collectionDetails.createdAt < versionTime) {
        previewImageKey = `collections/${userId}/${collectionId}/${mediaFileName}`;
      } else {
        let filename = mediaFileName.replace(/\.[^/.]+$/, "") + ".jpg";
        previewImageKey = `collections/${userId}/${collectionId}/optimized/${filename}`;
      }

      // Get token if it's a function, or use it directly if it's a string
      let authToken = token;
      if (typeof token === 'function') {
        authToken = await token();
      }

      // Check if we have a valid token
      if (!authToken) {
        toast.error("Authentication token not available");
        return;
      }

      // Call the backend to update the preview image
      const response = await CollectionsApi.updateCollection(authToken, {
        type: 'previewImage',
        collectionId: collectionId,
        data: previewImageKey
      });

      if (response.success) {
        toast.success("Preview image set successfully!");
      } else {
        toast.error(`Failed to set preview image: ${response.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error("Error setting preview image:", err);
      toast.error("Error setting preview image: " + (err.message || err));
    }
  };

  const getDownloadUrl = async (currentImageObj, index) => {
    let key;
    const mediaItem = media[index];
    const mediaFileName = mediaItem.fileName || mediaItem.f;

    // For download, always get the full image (without /optimized/)
    if (collectionDetails.createdAt < versionTime) {
      key = `collections/${userId}/${collectionId}/${mediaFileName}`;
    } else {
      let filename = mediaFileName.replace(/\.[^/.]+$/, "") + ".jpg";
      if (isVideoFile(mediaFileName)) {
        // keep original extension for videos
        filename = mediaFileName.replace(/\.[^/.]+$/, "") + mediaFileName.substring(mediaFileName.lastIndexOf('.'));
      }
      key = `collections/${userId}/${collectionId}/${filename}`; // Remove /optimized/ for full image
    }
    
    try {
      const s3 = new S3Client({
        region: "us-east-1",
        credentials: awsCredentials,
      });
      const command = new GetObjectCommand({
        Bucket: UPLOADS_BUCKET,
        Key: key,
      });
      const url = await getSignedUrl(s3, command, { expiresIn: 3600 });
      
      // Force download using fetch and blob to avoid opening in tab
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.status}`);
        }
        
        const blob = await response.blob();
        
        // Create object URL and force download
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = mediaFileName; // Use original filename for download
        link.style.display = 'none'; // Hide the link
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);
        
        toast.success(`Downloaded: ${mediaFileName}`);
      } catch (fetchError) {
        console.error("Error downloading image:", fetchError);
        toast.error("Failed to download image. Please try again.");
      }
      
      return url;
    } catch (err) {
      console.error("Error fetching download URL from S3:", err);
      toast.error("Failed to download image.");
      return "";
    }
  };

  const deleteMedia = async () => {
    if (!currentImage || currentIndex === null || !media || !media[currentIndex]) {
      toast.error("Unable to delete media");
      return;
    }

  const mediaItem = media[currentIndex];
  const mediaId = mediaItem.key || mediaItem.id;
    
    if (!mediaId) {
      toast.error("Media ID not found");
      return;
    }

    try {
      // Get token if it's a function, or use it directly if it's a string
      let authToken = token;
      if (typeof token === 'function') {
        authToken = await token();
      }

      // Check if we have a valid token
      if (!authToken) {
        toast.error("Authentication token not available");
        return;
      }

      if (isAdmin) {
        // Admin path: use admin endpoint for admin-scoped delete
        const res = await fetch(`${API_URL}/admin/v1/media`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ mediaId, userId }),
        });
        if (res.ok) {
          toast.success("Media deleted successfully!");
          if (onMediaDeleted) onMediaDeleted(mediaId);
          handleClose();
        } else {
          const data = await res.json().catch(() => ({}));
          toast.error(`Failed to delete media: ${data.message || res.status}`);
        }
      } else {
        // User path: use existing user endpoint
        const response = await CollectionsApi.deleteMedia(authToken, { collectionId, mediaId });
        if (response.success) {
          toast.success("Media deleted successfully!");
          if (onMediaDeleted) onMediaDeleted(mediaId);
          handleClose();
        } else {
          toast.error(`Failed to delete media: ${response.message || 'Unknown error'}`);
        }
      }
    } catch (error) {
      console.error("Error deleting media:", error);
      toast.error("Error deleting media: " + (error.message || error));
    }
  };


  const handleClose = () => {
    setCurrentImage(null);
    setCurrentIndex(null);
    setShowProcessingScreen(false);
    setRecoveryMode(false);
    setRecoveryInFlight(false);
    onClose();
  };

  /**
   * SS-461: Re-invoke the S3 processor for a FAILED media item. The backend
   * endpoint POST /media/v1/media/{mediaId}/redrive returns 200 (started),
   * 409 (status changed concurrently or not eligible), or 410 (TEMP source
   * expired). On 200, close the modal and let the parent refresh — the
   * next status poll will pick up PROCESSING → SUCCESS.
   */
  const redriveCurrent = async () => {
    if (!currentIndex && currentIndex !== 0) {
      toast.error("Unable to redrive: no media selected");
      return;
    }
    const mediaItem = media?.[currentIndex];
    if (!mediaItem) {
      toast.error("Unable to redrive: media not found");
      return;
    }
    const mediaId = mediaItem.key || mediaItem.id;
    if (!mediaId) {
      toast.error("Media ID not found on this item");
      return;
    }
    let authToken = token;
    if (typeof token === 'function') {
      authToken = await token();
    }
    if (!authToken) {
      toast.error("Authentication token not available");
      return;
    }
    setRecoveryInFlight(true);
    try {
      const response = await CollectionsApi.redriveMedia(authToken, mediaId);
      // SS-470: apiClient returns { success, message, status, data } for ALL
      // responses (including 4xx/5xx) — it does NOT throw on HTTP errors.
      // Inspect the response envelope directly.
      if (response?.success) {
        toast.success("Redrive started. Status will refresh on next poll.");
        if (onMediaDeleted) onMediaDeleted(mediaId);
        handleClose();
        return;
      }
      const code = response?.status;
      if (code === 410) {
        toast.error("Source object expired. Please re-upload this file.");
      } else if (code === 409) {
        toast.warn("Status changed concurrently; refreshing.");
        if (onMediaDeleted) onMediaDeleted(mediaId);
        handleClose();
      } else if (code === 404) {
        toast.error("Media not found. It may have been deleted.");
      } else if (code === 401 || code === 403) {
        toast.error("Not authorized to redrive this item.");
      } else {
        toast.warn(`Redrive did not start: ${response?.message || 'unknown reason'}`);
      }
    } catch (err) {
      // Network / unexpected failure (apiClient only throws on these).
      toast.error(`Redrive failed: ${err?.message || 'unknown error'}`);
    } finally {
      setRecoveryInFlight(false);
    }
  };

  const goToPrevious = () => {
    if (!media || media.length === 0) return;

    const prevIndex = currentIndex - 1;
    if (prevIndex >= 0) {
      const mediaItem = media[prevIndex];
      if (mediaItem.status === "SUCCESS" || mediaItem.status === "PROCESSING" || mediaItem.status === "SUCCESS_NO_OPTIMIZATION") {
        setCurrentIndex(prevIndex);
        getImageUrl(mediaItem, prevIndex);
      } else {
        const statusMessage = mediaItem.status === "FAILED" 
          ? "This media failed to process."
          : "This media is still processing.";
        toast.warn(statusMessage);
      }
    }
  };

  const goToNext = () => {
    if (!media || media.length === 0) return;

    const nextIndex = currentIndex + 1;
    if (nextIndex < media.length) {
      const mediaItem = media[nextIndex];
      if (mediaItem.status === "SUCCESS" || mediaItem.status === "PROCESSING" || mediaItem.status === "SUCCESS_NO_OPTIMIZATION") {
        setCurrentIndex(nextIndex);
        getImageUrl(mediaItem, nextIndex);
      } else {
        const statusMessage = mediaItem.status === "FAILED" 
          ? "This media failed to process."
          : "This media is still processing.";
        toast.warn(statusMessage);
      }
    }
  };

  const hasPrev = media && media.length > 0 && currentIndex > 0 && media.slice(0, currentIndex).some(p => p.status === 'SUCCESS' || p.status === 'PROCESSING' || p.status === 'SUCCESS_NO_OPTIMIZATION');
  const hasNext = media && media.length > 0 && currentIndex < media.length - 1 && media.slice(currentIndex + 1).some(p => p.status === 'SUCCESS' || p.status === 'PROCESSING' || p.status === 'SUCCESS_NO_OPTIMIZATION');


  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: '95vw',
          height: '95vh',
          maxWidth: 'none',
          maxHeight: 'none',
          m: 1,
          display: 'flex',
          flexDirection: 'column',
        }
      }}
    >
      <DialogTitle sx={{ flexShrink: 0, py: { xs: 1, sm: 2 } }}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item xs>
            <Box>
              <Typography variant="h6" component="div" noWrap title={currentImage?.fileName || (media && currentIndex !== null && (media[currentIndex]?.fileName || media[currentIndex]?.f)) || ''}>
                {currentImage?.fileName || (media && currentIndex !== null && (media[currentIndex]?.fileName || media[currentIndex]?.f)) || 'Media'}
              </Typography>
              {currentImage && (currentImage.status || showProcessingScreen) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  {(currentImage.status === 'PROCESSING' || showProcessingScreen) && (
                    <>
                      <CircularProgress size={12} />
                      <Typography variant="caption" color="orange">
                        {isVideoFile(currentImage.fileName) ? 'Video optimization in progress' : 'Processing...'}
                      </Typography>
                    </>
                  )}
                  {currentImage.status === 'SUCCESS_NO_OPTIMIZATION' && !showProcessingScreen && (
                    <Typography variant="caption" color="text.secondary">
                      {isVideoFile(currentImage.fileName) ? 'Basic video version' : 'Basic version'}
                    </Typography>
                  )}
                  {!currentImage.isOptimized && currentImage.status === 'SUCCESS' && !showProcessingScreen && (
                    <Typography variant="caption" color="text.secondary">
                      Original quality
                    </Typography>
                  )}
                  {currentImage.status === 'FAILED' && recoveryMode && (
                    <Typography variant="caption" color="error">
                      Processing failed — recover below
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Grid>
          <Grid item>
            {recoveryMode ? (
              // SS-461: Redrive + Delete for failed/stuck media items.
              <>
                <Button
                  onClick={redriveCurrent}
                  variant="contained"
                  color="primary"
                  size="small"
                  disabled={recoveryInFlight}
                  sx={{
                    m: { xs: 0.5, sm: 1 },
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    px: { xs: 1, sm: 2 },
                  }}
                >
                  {recoveryInFlight ? 'Redriving…' : 'Redrive'}
                </Button>
                <Button
                  onClick={deleteMedia}
                  variant="contained"
                  color="error"
                  size="small"
                  disabled={recoveryInFlight}
                  startIcon={<DeleteIcon />}
                  sx={{
                    m: { xs: 0.5, sm: 1 },
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    px: { xs: 1, sm: 2 },
                  }}
                >
                  Delete
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => getDownloadUrl(currentImage, currentIndex)}
                  variant="contained"
                  size="small"
                  disabled={loadingImage || !currentImage || currentIndex === null || showProcessingScreen}
                  sx={{
                    m: { xs: 0.5, sm: 1 },
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    px: { xs: 1, sm: 2 },
                  }}
                >
                  Download
                </Button>
                <Button
                  onClick={setAsPreviewImage}
                  variant="outlined"
                  size="small"
                  disabled={loadingImage || !currentImage || currentIndex === null || showProcessingScreen}
                  sx={{
                    m: { xs: 0.5, sm: 1 },
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    px: { xs: 1, sm: 2 },
                  }}
                >
                  Set as Preview
                </Button>
                {/* Delete button - show for users who can delete (own media) or admins */}
                <Button
                  onClick={deleteMedia}
                  variant="contained"
                  color="error"
                  size="small"
                  disabled={loadingImage || !currentImage || currentIndex === null || showProcessingScreen}
                  startIcon={<DeleteIcon />}
                  sx={{
                    m: { xs: 0.5, sm: 1 },
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    px: { xs: 1, sm: 2 },
                  }}
                >
                  Delete
                </Button>
              </>
            )}
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                color: (theme) => theme.palette.grey[500],
                ml: { xs: 0.5, sm: 1 },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent 
        dividers 
        sx={{ 
          p: 0, 
          overflow: "hidden", 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          flex: 1,
          height: 0, // Force flex child to respect parent constraints
        }}
      >
        {recoveryMode ? (
          // SS-461: dedicated recovery panel for FAILED media items. Shows
          // the file name, the failure reason (if surfaced from the backend),
          // and the Redrive / Delete actions. The arrows and preview are not
          // available because there is no image to display.
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            p: 4,
            backgroundColor: 'background.paper',
            borderRadius: 2,
            border: '2px dashed',
            borderColor: 'error.main',
            minHeight: '300px',
            maxWidth: '560px',
            m: 'auto',
            gap: 2,
          }}>
            <Typography variant="h5" gutterBottom color="error">
              Processing failed
            </Typography>
            <Typography variant="body1" color="text.primary">
              {currentImage?.fileName || 'This media'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              We were unable to optimize this file. You can retry the processing
              (Redrive) or remove it from the collection (Delete).
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button
                onClick={redriveCurrent}
                variant="contained"
                color="primary"
                disabled={recoveryInFlight}
                data-testid="recovery-redrive"
              >
                {recoveryInFlight ? 'Redriving…' : 'Redrive processing'}
              </Button>
              <Button
                onClick={deleteMedia}
                variant="outlined"
                color="error"
                disabled={recoveryInFlight}
                startIcon={<DeleteIcon />}
                data-testid="recovery-delete"
              >
                Delete this media
              </Button>
            </Box>
          </Box>
        ) : loadingImage ? (
          <Box sx={{ 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center", 
            height: '100%',
            width: '100%'
          }}>
            <CircularProgress size={60} />
          </Box>
        ) : (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            width: '100%', 
            height: '100%',
            position: 'relative',
            minHeight: 0, // Allow shrinking
          }}>
            {/* Left arrow - overlays on image */}
            {hasPrev && (
              <IconButton 
                onClick={goToPrevious} 
                size="large"
                sx={{
                  position: 'absolute',
                  left: { xs: 8, sm: 16 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 3, // Higher z-index to ensure it's above image
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  border: '2px solid rgba(255, 255, 255, 0.2)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.9)',
                    border: '2px solid rgba(255, 255, 255, 0.4)',
                    transform: 'translateY(-50%) scale(1.1)',
                  },
                  width: { xs: 44, sm: 52 },
                  height: { xs: 44, sm: 52 },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <FontAwesomeIcon icon={faArrowCircleLeft} size="lg" />
              </IconButton>
            )}
            
            {/* Image container - fills available space completely */}
            <Box sx={{ 
              width: '100%',
              height: '100%',
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              padding: '0', // Remove all padding to allow full expansion
              minHeight: 0, // Allow shrinking
            }}>
              {showProcessingScreen ? (
                // Processing screen for videos that aren't ready yet
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  p: 4,
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  borderRadius: 2,
                  border: '2px dashed',
                  borderColor: 'grey.300',
                  minHeight: '300px',
                  maxWidth: '500px',
                }}>
                  <CircularProgress size={60} sx={{ mb: 3, color: 'primary.main' }} />
                  <Typography variant="h6" gutterBottom color="text.primary">
                    Video Processing in Progress
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    This video is still being optimized for web playback.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Please check back in a few minutes once processing is complete.
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      // Refresh by trying to load the image again
                      if (currentImage && media && currentIndex !== null) {
                        const mediaItem = media[currentIndex];
                        getImageUrl(mediaItem, currentIndex);
                      }
                    }}
                    sx={{ mt: 3 }}
                  >
                    Check Again
                  </Button>
                </Box>
              ) : currentImage && (
                <>
                  {isVideoFile(currentImage.fileName) && currentImage.url ? (
                    <video
                      src={currentImage.url}
                      controls
                      autoPlay={false}
                      muted
                      style={{ 
                        maxHeight: '100%', 
                        maxWidth: '100%', 
                        objectFit: 'contain',
                        borderRadius: '4px',
                        width: 'auto',
                        height: 'auto',
                      }}
                      onError={(e) => {
                        console.error("Video load error:", e);
                        toast.warn("Video still processing. Please check back later.");
                      }}
                    >
                      Your browser does not support the video tag.
                    </video>
                  ) : !isVideoFile(currentImage.fileName) && currentImage.url ? (
                    <Img
                      src={currentImage.url}
                      style={{ 
                        maxHeight: '100%', 
                        maxWidth: '100%', 
                        objectFit: 'contain',
                        borderRadius: '4px',
                        width: 'auto',
                        height: 'auto',
                      }}
                      loader={<CircularProgress />}
                    />
                  ) : null}
                </>
              )}
            </Box>
            
            {/* Right arrow - overlays on image */}
            {hasNext && (
              <IconButton 
                onClick={goToNext} 
                size="large"
                sx={{
                  position: 'absolute',
                  right: { xs: 8, sm: 16 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 3, // Higher z-index to ensure it's above image
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  border: '2px solid rgba(255, 255, 255, 0.2)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.9)',
                    border: '2px solid rgba(255, 255, 255, 0.4)',
                    transform: 'translateY(-50%) scale(1.1)',
                  },
                  width: { xs: 44, sm: 52 },
                  height: { xs: 44, sm: 52 },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <FontAwesomeIcon icon={faArrowCircleRight} size="lg" />
              </IconButton>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ImagePreviewModal;
